import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import LessonSelectedCard from './components/LessonSelectedCard';
import LessonSelectionDialog from './components/LessonSelectionDialog';
import TextInputMode from './components/TextInputMode';
import useTextInputOrLessonSelector from './hooks/useTextInputOrLessonSelector';
import './textInputOrLessonSelector.scss';

const TextInputOrLessonSelector = React.forwardRef(function TextInputOrLessonSelectorComponent(
  {
    value = { mode: 'text', text: '', lesson: null },
    onChange,
    wordLimit = 2000,
    label,
    placeholder,
    required = false,
    disabled = false,
    showLabel = true,
    name = 'text-or-lesson-selector',
  },
  ref
) {
  const {
    mode,
    textValue,
    selectedLesson,
    isDialogOpen,
    wordCount,
    courses,
    units,
    loadingUnits,
    loadingLessonContent,
    contentError,
    handleTextChange,
    handleLessonSelect,
    handleRemoveLesson,
    openDialog,
    closeDialog,
    loadCourseUnits,
  } = useTextInputOrLessonSelector({ value, onChange, wordLimit });

  const { t } = useTranslation(['chatbot']);

  return (
    <div className="text-input-or-lesson-selector" ref={ref}>
      {showLabel && label && (
        <label className="text-input-or-lesson-selector__label label">
          {label}
          {required && <sup> *</sup>}
        </label>
      )}
      <div className="text-input-or-lesson-selector__content">
        {mode === 'text' ? (
          <TextInputMode
            value={textValue}
            onChange={handleTextChange}
            placeholder={placeholder || t('chatbot:Write, paste text or choose from your books')}
            wordCount={wordCount}
            wordLimit={wordLimit}
            onOpenDialog={openDialog}
            disabled={disabled}
            name={name}
          />
        ) : (
          <LessonSelectedCard
            lesson={selectedLesson}
            onRemove={handleRemoveLesson}
            disabled={disabled}
            isLoadingContent={loadingLessonContent}
            error={contentError}
          />
        )}
        {contentError && <div className="text-input-or-lesson-selector__error-message">{t('chatbot:teacher_tools_error_content_unavailable_message')}</div>}
      </div>

      <LessonSelectionDialog
        open={isDialogOpen}
        onClose={closeDialog}
        onSelect={handleLessonSelect}
        courses={courses}
        units={units}
        loadingUnits={loadingUnits}
        loadCourseUnits={loadCourseUnits}
        contentError={contentError}
      />
    </div>
  );
});

TextInputOrLessonSelector.propTypes = {
  value: PropTypes.shape({
    mode: PropTypes.oneOf(['text', 'lesson']),
    text: PropTypes.string,
    lesson: PropTypes.shape({
      guid: PropTypes.string,
      title: PropTypes.string,
      wordCount: PropTypes.number,
      courseName: PropTypes.string,
      unitName: PropTypes.string,
    }),
  }),
  onChange: PropTypes.func.isRequired,
  characterLimit: PropTypes.number,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  showLabel: PropTypes.bool,
  name: PropTypes.string,
  wordLimit: PropTypes.number,
};

export default TextInputOrLessonSelector;
